import { useEffect, useRef } from 'react';
import { useCookieConsent } from '@/contexts/CookieConsentContext';
import { deleteAllDataForCategory, deleteCookiesByPattern, clearLocalStorageByPattern, clearSessionStorageByPattern } from '@/lib/cookie-utils';

interface ScriptConfig {
  id: string;
  src?: string;
  innerHTML?: string;
  async?: boolean;
  defer?: boolean;
  strategy?: 'afterInteractive' | 'lazyOnload' | 'beforeInteractive';
  onLoad?: () => void;
  onError?: () => void;
  cookiePatterns?: {
    cookies?: string[];
    localStorage?: string[];
    sessionStorage?: string[];
  };
}

interface ScriptLoaderProps {
  category: 'analytics' | 'marketing' | 'preferences';
  script: ScriptConfig;
  enabled?: boolean;
}

/**
 * ScriptLoader - Conditionally loads scripts based on cookie consent
 * 
 * This component ensures GDPR compliance by:
 * - Only loading scripts after user consent
 * - Removing scripts when consent is withdrawn
 * - Respecting Do Not Track browser settings
 */
export function ScriptLoader({ category, script, enabled = true }: ScriptLoaderProps) {
  const { consent, hasDoNotTrack } = useCookieConsent();
  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const loadedRef = useRef<boolean>(false);

  useEffect(() => {
    // Check if we have consent for this category
    const hasConsent = consent[category];
    const wasLoaded = loadedRef.current;
    
    // Don't load if:
    // 1. No consent for this category
    // 2. Do Not Track is enabled
    // 3. Script is disabled via props
    if (!hasConsent || hasDoNotTrack || !enabled) {
      // If script was previously loaded but consent is now withdrawn, remove it
      if (wasLoaded || scriptRef.current) {
        removeScript();
        // Delete all cookies and storage for this category when consent is withdrawn
        if (!hasConsent) {
          deleteAllDataForCategory(category);
        }
      }
      return;
    }
    
    // Only load if not already loaded
    if (!loadedRef.current) {
      loadScript();
    }

    // Cleanup on unmount or consent change
    return () => {
      if (scriptRef.current) {
        removeScript();
      }
    };
  }, [consent, category, hasDoNotTrack, enabled, script]);

  const loadScript = () => {
    // Prevent duplicate script loading
    const existingScript = document.getElementById(script.id);
    if (existingScript) {
      scriptRef.current = existingScript as HTMLScriptElement;
      loadedRef.current = true;
      return;
    }

    const scriptElement = document.createElement('script');
    scriptElement.id = script.id;
    
    // Set script attributes
    if (script.src) {
      scriptElement.src = script.src;
    }
    
    if (script.innerHTML) {
      scriptElement.innerHTML = script.innerHTML;
    }
    
    if (script.async !== undefined) {
      scriptElement.async = script.async;
    }
    
    if (script.defer !== undefined) {
      scriptElement.defer = script.defer;
    }

    // Add event handlers
    if (script.onLoad) {
      scriptElement.onload = script.onLoad;
    }
    
    if (script.onError) {
      scriptElement.onerror = script.onError;
    }

    // Determine where to append based on strategy
    const appendTarget = script.strategy === 'beforeInteractive' 
      ? document.head 
      : document.body;

    // Add script to DOM
    appendTarget.appendChild(scriptElement);
    scriptRef.current = scriptElement;
    loadedRef.current = true;
  };

  const removeScript = () => {
    if (scriptRef.current) {
      try {
        scriptRef.current.remove();
        
        // Clean up any global variables or functions the script may have created
        // This is script-specific and would need to be expanded based on actual scripts
        cleanupScriptGlobals(script.id, script.cookiePatterns);
        
        scriptRef.current = null;
        loadedRef.current = false;
      } catch (error) {
        // Script removal failed silently
      }
    }
  };

  const cleanupScriptGlobals = (scriptId: string, cookiePatterns?: ScriptConfig['cookiePatterns']) => {
    // Clean up cookies and storage if patterns are provided
    if (cookiePatterns) {
      if (cookiePatterns.cookies) {
        deleteCookiesByPattern(cookiePatterns.cookies);
      }
      if (cookiePatterns.localStorage) {
        clearLocalStorageByPattern(cookiePatterns.localStorage);
      }
      if (cookiePatterns.sessionStorage) {
        clearSessionStorageByPattern(cookiePatterns.sessionStorage);
      }
    }
    
    // Clean up global variables based on script ID
    // This needs to be customized for each third-party script
    switch (scriptId) {
      case 'google-analytics':
      case 'google-analytics-gtag':
      case 'google-analytics-config':
        // Clean up Google Analytics globals and cookies
        if (typeof window !== 'undefined') {
          const win = window as any;
          delete win.ga;
          delete win.gtag;
          delete win.dataLayer;
          delete win.google_tag_manager;
        }
        break;
        
      case 'facebook-pixel':
        // Clean up Facebook Pixel globals and cookies
        if (typeof window !== 'undefined') {
          const win = window as any;
          delete win.fbq;
          delete win._fbq;
        }
        break;
        
      case 'hotjar':
        // Clean up Hotjar globals
        if (typeof window !== 'undefined') {
          const win = window as any;
          delete win.hj;
          delete win._hjSettings;
        }
        break;
        
      case 'mixpanel':
        // Clean up Mixpanel globals
        if (typeof window !== 'undefined') {
          const win = window as any;
          delete win.mixpanel;
        }
        break;
        
      case 'intercom':
        // Clean up Intercom globals
        if (typeof window !== 'undefined') {
          const win = window as any;
          delete win.Intercom;
          delete win.intercomSettings;
        }
        break;
        
      case 'google-ads':
      case 'google-ads-config':
        // Clean up Google Ads globals
        if (typeof window !== 'undefined') {
          const win = window as any;
          delete win.google_trackConversion;
        }
        break;
        
      // Add more cleanup cases as needed
      default:
        break;
    }
  };

  // This component doesn't render anything
  return null;
}

// Helper hook to check if a script can be loaded
export function useCanLoadScript(category: 'analytics' | 'marketing' | 'preferences'): boolean {
  const { consent, hasDoNotTrack } = useCookieConsent();
  return consent[category] && !hasDoNotTrack;
}